import { authApiService, type LanguageInfo } from './api/auth'

// Processed language info with id and value for compatibility
export interface ProcessedLanguageInfo extends LanguageInfo {
  id: string
  name: string
  value: string
}

let activeLangs: ProcessedLanguageInfo[] = []
let allLangs: ProcessedLanguageInfo[] = []

/**
 * Process languages to maintain compatibility with previous frontend structure.
 * Converts localeTag to id and creates a value field with native/english names.
 */
function processLanguages(languages: LanguageInfo[]): ProcessedLanguageInfo[] {
  return languages.map((language) => ({
    ...language,
    id: language.localeTag,
    name: language.nativeName,
    value:
      language.nativeName !== language.englishName
        ? `${language.nativeName} / ${language.englishName}`
        : language.nativeName
  }))
}

/**
 * Get all languages (both active and inactive).
 * Results are cached unless fetchFromServer is true.
 */
export async function getAllLanguages(fetchFromServer = false): Promise<ProcessedLanguageInfo[]> {
  if (fetchFromServer || !allLangs.length) {
    const data = await authApiService.getAllLanguages()
    const langs = processLanguages(data)
    allLangs = langs
    return langs
  }

  return allLangs
}

/**
 * Get only active/allowed languages.
 * Results are cached unless fetchFromServer is true.
 */
export async function getAllowedLanguages(fetchFromServer = false): Promise<ProcessedLanguageInfo[]> {
  if (fetchFromServer || !activeLangs.length) {
    const data = await getAllLanguages(true)
    const langs = data.filter((language) => language.active)
    activeLangs = langs
    return langs
  }

  return activeLangs
}

/**
 * Clear cached language data (useful for testing or force refresh).
 */
export function clearLanguageCache(): void {
  activeLangs = []
  allLangs = []
}

export const languageService = {
  getAllLanguages,
  getAllowedLanguages,
  clearLanguageCache
}
